import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext.js';
import { LogIn, Shield, Bot } from 'lucide-react';
import { Button, buttonVariants } from '@randompedestrian07/ui';
import { Alert, AlertDescription } from '@randompedestrian07/ui';

export const LoginPage: React.FC = () => {
  const { login, isAuthenticated, isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get('error');

    if (errorParam) {
      switch (errorParam) {
        case 'discord_oauth_error':
          setError('Discord login failed. Please try again.');
          break;
        case 'missing_code':
          setError('Login failed due to missing authorization code.');
          break;
        case 'not_guild_member':
          setError('You must be a member of the authorized Discord server to access this dashboard.');
          break;
        case 'auth_failed':
          setError('Authentication failed. Please try again.');
          break;
        default:
          setError('An unknown error occurred during login.');
      }

      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const success = urlParams.get('login');
    if (success === 'success') {
      window.location.href = '/';
    }
  }, []);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      window.location.href = '/';
    }
  }, [isAuthenticated, isLoading]);

  const handleLogin = () => {
    setError(null);
    login();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-bg-solid flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
          <p className="text-muted">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-solid flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="p-3 bg-accent rounded-full">
              <Bot size={32} className="text-bg-solid" />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-text mb-2">
            Outer Haven Bots
          </h2>
          <p className="text-muted text-lg">
            Admin Dashboard
          </p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="bg-panel-strong border border-stroke rounded-lg p-8 space-y-6">
          <div className="text-center">
            <Shield size={48} className="mx-auto text-accent mb-4" />
            <h3 className="text-xl font-semibold text-text mb-2">
              Secure Login
            </h3>
            <p className="text-muted text-sm">
              Log in with your Discord account to access the admin dashboard.
              You must be a member of the authorized server.
            </p>
          </div>

          <Button
            onClick={handleLogin}
            disabled={isLoading}
            className={buttonVariants({ variant: 'default', className: 'w-full flex items-center justify-center space-x-3' })}
          >
            <LogIn size={20} />
            <span>Login with Discord</span>
          </Button>

          <div className="text-center">
            <p className="text-xs text-muted">
              By logging in, you agree to the authorized use of this admin panel.
              Only members of the specified Discord server can access this dashboard.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 text-center">
          <div className="bg-panel-strong/50 border border-stroke rounded-md p-4">
            <h4 className="text-text font-medium mb-1">Bot Management</h4>
            <p className="text-muted text-xs">Configure and monitor your Discord bots</p>
          </div>
          <div className="bg-panel-strong/50 border border-stroke rounded-md p-4">
            <h4 className="text-text font-medium mb-1">LLM Providers</h4>
            <p className="text-muted text-xs">Manage AI providers and routing</p>
          </div>
          <div className="bg-panel-strong/50 border border-stroke rounded-md p-4">
            <h4 className="text-text font-medium mb-1">Permissions</h4>
            <p className="text-muted text-xs">Control access and user permissions</p>
          </div>
        </div>

        <div className="text-center">
          <p className="text-xs text-muted/60">
            Outer Haven Bots Admin
          </p>
        </div>
      </div>
    </div>
  );
};
